// `courses-keys` (spec section 6.7): the one place the rest of the app reads a
// member's Next Steps key material and enrolment state from. It is hydrated
// from the key store as soon as the signed-in member is known (App.vue), in the
// startup order of section 6.2: the IndexedDB record, else the platform copy
// (which means this install has to register itself again), else nothing. A
// read that must not race an empty store awaits `courses-keys/ready`. Logout
// dispatches `wipe`, which removes the member's record and clears this state
// and leaves the platform copy alone: that copy belongs to the phone's owner.
//
// Whenever the device identity changes, `courses-devices` is told what this
// install may register as (`setLocalDevice`, section 6.6): the persisted id
// and public key with the platform's label and kind, and never the private
// key. That module refuses to register until it has been told.
//
// Every async step is guarded by a generation counter: a wipe or a member
// switch bumps it, and a hydration or persist that was in flight commits
// nothing afterwards, so the previous member's key can never land back in
// state after they signed out.
//
// THE ENROLMENT ATTEMPT IS STATE, NOT KEY MATERIAL (section 6.7, decision
// 40). A provisional attempt lives in the key store's second object store and
// is reported here only as `attemptState`; `hasMasterKey` and `keyMaterial`
// never see it, so a member mid-attempt is byte-for-byte an unenrolled member
// until `promoteAttempt` — one store transaction — makes it the key record.
import { ATTEMPT_STATE, WIPE_SCOPE, openKeyStore } from '@/utils/courses-crypto/key-store';
import { assertMemberId } from '@/utils/courses-crypto/identifiers';
import { isMasterKey } from '@/utils/courses-crypto/master-key';
import {
  PLATFORM_COPY_SLOT,
  parsePlatformCopy,
  resolvePlatformAdapter,
  serialisePlatformCopy,
  serialisePlatformTombstone,
} from '@/utils/courses-crypto/platform-key-adapter';
import {
  FALLBACK_DEVICE_NAME,
  describeThisDevice,
  thisPlatform,
} from '@/utils/courses-encryption/this-device';

/**
 * How long a hydration waits for the platform's device label before handing
 * over the generic one. A bridge call that never answers must not hold the
 * keys, and with them every read that awaits `ready()`.
 */
export const DEVICE_DESCRIPTION_TIMEOUT_MS = 2000;

export const ENROLMENT_STATUS = Object.freeze({
  /** Not yet asked the API. */
  UNKNOWN: 'unknown',
  NOT_ENROLLED: 'not-enrolled',
  /** Enrolled and this device holds MK. */
  ENROLLED: 'enrolled',
  /** Enrolled elsewhere; this device is waiting for approval or a recovery code. */
  PENDING: 'pending',
});

export const HYDRATION_STATUS = Object.freeze({
  /** No member yet, or wiped: `ready()` waits for the next hydration. */
  IDLE: 'idle',
  HYDRATING: 'hydrating',
  HYDRATED: 'hydrated',
  /** The last hydration threw; `hydrate` again to retry. */
  FAILED: 'failed',
});

/**
 * What the member's enrolment attempt is, as hydration read it or as the last
 * attempt action left it. `none` is this module's word for "no row"; the
 * other three are the key store's own states.
 */
export const ATTEMPT_STATUS = Object.freeze({
  NONE: 'none',
  ...ATTEMPT_STATE,
});

/** What `wipe` is asked to forget; defined beside the key store, re-exported here. */
export { WIPE_SCOPE };
const WIPE_SCOPES = Object.values(WIPE_SCOPE);

export const COURSES_KEYS_ERRORS = Object.freeze({
  HYDRATION_FAILED: 'HYDRATION_FAILED',
  /** The key store cannot keep a key between launches; enrolment must not proceed. */
  NOT_PERSISTENT: 'NOT_PERSISTENT',
  /** The keys belong to another member than the one signed in, or the member left. */
  WRONG_MEMBER: 'WRONG_MEMBER',
  /** An attempt action found no attempt row to act on. */
  NO_ATTEMPT: 'NO_ATTEMPT',
  /** A rotation was asked for on a device with no key record to rotate from. */
  NO_RECORD: 'NO_RECORD',
  /**
   * This device already holds a pending generation with a DIFFERENT id, and
   * writing another over it would be the only copy of the first one gone.
   */
  PENDING_HELD: 'PENDING_HELD',
  TIMEOUT: 'TIMEOUT',
});

export class CoursesKeysError extends Error {
  /**
   * @param {keyof typeof COURSES_KEYS_ERRORS} code
   * @param {string} message
   */
  constructor(code, message) {
    super(message);
    this.name = 'CoursesKeysError';
    this.code = code;
  }
}

const ENROLMENT_STATUSES = Object.values(ENROLMENT_STATUS);
const ATTEMPT_STATUSES = Object.values(ATTEMPT_STATUS);

const initialState = () => ({
  memberId: null,
  keyId: null,
  mk: null,
  deviceId: null,
  devicePublicKey: null,
  devicePrivateKey: null,
  /**
   * The out-of-band approval secret S this install generated beside its key
   * pair (#1693). Part of the DEVICE IDENTITY, not of the key: a device with
   * no MK at all still has one, and that is the whole point — it is what the
   * waiting device shows on screen 2 for another phone to read.
   */
  approvalSecret: null,
  status: HYDRATION_STATUS.IDLE,
  hydrationError: null,
  /** False when the key store fell back to memory for this session. */
  persistent: false,
  /** MK came from the platform copy: this install must register as a new device. */
  needsRegistration: false,
  enrolmentStatus: ENROLMENT_STATUS.UNKNOWN,
  hasRecoveryCode: false,
  /** The provisional enrolment attempt's state. Never key material. */
  attemptState: ATTEMPT_STATUS.NONE,
  /**
   * The NEXT generation, held between a rotation's begin and its commit
   * (#1694, spec 6.12). Both null except during one.
   *
   * Beside `mk` rather than replacing it, because that is what makes a
   * rotation resumable and safe to abandon: MK1 stays readable for every row
   * that has not been re-sealed yet, on this device and on every other, until
   * commit says otherwise.
   */
  pendingMk: null,
  pendingKeyId: null,
  /**
   * Which OTHER devices the pending rotation keeps (api#1156): the ids the
   * commit re-wraps MK2 to instead of revoking. Empty is the ordinary sign-out
   * of every other device. Held with the pending generation, so a rotation
   * resumed after a relaunch keeps the same devices the member chose — and
   * only on this device: another device finishing it from the recovery code
   * knows nothing of the choice and signs everyone else out, which errs on
   * the side of fewer devices holding the key, never more.
   */
  pendingKeepDeviceIds: [],
});

/**
 * A promise that later hydrations can resolve or reject once. Rejections are
 * observed here so an unawaited gate never surfaces as an unhandled rejection;
 * every caller of `ready()` still receives them.
 *
 * @returns {{ promise: Promise<void>, settled: boolean, resolve: () => void, reject: (error: Error) => void }}
 */
function createGate() {
  const gate = { settled: false };
  gate.promise = new Promise((resolve, reject) => {
    gate.resolve = () => {
      gate.settled = true;
      resolve();
    };
    gate.reject = error => {
      gate.settled = true;
      reject(error);
    };
  });
  gate.promise.catch(() => {});
  return gate;
}

/**
 * @param {unknown} bytes
 */
function zeroBytes(bytes) {
  if (bytes && typeof bytes.fill === 'function') bytes.fill(0);
}

/**
 * @param {object} [deps]
 * @param {() => Promise<import('@/utils/courses-crypto/key-store').KeyStore>} [deps.openStore]
 * @param {() => import('@/utils/courses-crypto/platform-key-adapter').PlatformKeyAdapter} [deps.resolveAdapter]
 * @param {number} [deps.describeTimeoutMs] the device-label budget, shortened in specs
 * @returns {import('vuex').Module<ReturnType<typeof initialState>, unknown>}
 */
export function createCoursesKeysModule({
  openStore = openKeyStore,
  resolveAdapter = resolvePlatformAdapter,
  describeTimeoutMs = DEVICE_DESCRIPTION_TIMEOUT_MS,
} = {}) {
  // Non-reactive bookkeeping: the open store, the in-flight hydration, the
  // generation every async step checks before committing, and the gate a read
  // waits on until the current member's hydration has run.
  let storePromise = null;
  let hydration = { memberId: null, promise: null };
  let generation = 0;
  let gate = createGate();

  const getStore = () => {
    if (!storePromise) {
      storePromise = openStore().catch(error => {
        storePromise = null;
        throw error;
      });
    }
    return storePromise;
  };

  /** A settled gate belongs to a finished hydration; the next one needs its own. */
  const renewGate = () => {
    if (gate.settled) gate = createGate();
  };

  /**
   * @param {number} memberId
   * @returns {Promise<{ record: object | null, persistent: boolean }>}
   */
  const readRecord = async memberId => {
    try {
      const store = await getStore();
      return { record: await store.get(memberId), persistent: store.persistent };
    } catch (error) {
      console.warn('[CoursesKeys] Could not read the key store:', error);
      return { record: null, persistent: false };
    }
  };

  /**
   * The attempt row's state, for hydration. Only the state: the row's bytes
   * are handed out by `loadAttempt` to the enrolment path and to nothing else.
   *
   * @param {number} memberId
   * @returns {Promise<string>} one of ATTEMPT_STATUS
   */
  const readAttemptState = async memberId => {
    try {
      const store = await getStore();
      const attempt = await store.getAttempt(memberId);
      return attempt?.state ?? ATTEMPT_STATUS.NONE;
    } catch (error) {
      console.warn('[CoursesKeys] Could not read the enrolment attempt:', error);
      return ATTEMPT_STATUS.NONE;
    }
  };

  /**
   * The checks every write shares: the row is the hydrated member's, and the
   * store can keep it between launches.
   *
   * @param {ReturnType<typeof initialState>} state
   * @param {unknown} memberId
   * @returns {Promise<import('@/utils/courses-crypto/key-store').KeyStore>}
   */
  const writableStoreFor = async (state, memberId) => {
    assertMemberId(memberId);
    if (memberId !== state.memberId) {
      throw new CoursesKeysError(
        COURSES_KEYS_ERRORS.WRONG_MEMBER,
        'Keys belong to another member than the one signed in',
      );
    }
    const store = await getStore();
    if (!store.persistent) {
      throw new CoursesKeysError(
        COURSES_KEYS_ERRORS.NOT_PERSISTENT,
        'This device cannot keep a key between launches',
      );
    }
    return store;
  };

  const signedOutMeanwhile = () =>
    new CoursesKeysError(
      COURSES_KEYS_ERRORS.WRONG_MEMBER,
      'The member signed out before the keys were saved',
    );

  /**
   * A compensation that itself failed. The write it was undoing belongs to a
   * member who has signed out, and the next sign-out sweeps whatever it left;
   * the caller is already being told WRONG_MEMBER.
   */
  const warnCompensation = error => {
    console.warn('[CoursesKeys] A write made after sign-out could not be undone:', error);
  };

  /**
   * @param {import('@/utils/courses-crypto/platform-key-adapter').PlatformKeyAdapter} adapter
   * @param {number} memberId
   * @returns {Promise<{ keyId: string, mk: Uint8Array } | null>}
   */
  const readPlatformCopy = async (adapter, memberId, slot = undefined) => {
    // A copy that is absent or not this member's parses to null; a store that
    // cannot be read at all is an error, and hydration fails loudly rather
    // than reporting a key that may be sitting right there as absent.
    return parsePlatformCopy(
      slot === undefined ? await adapter.get(memberId) : await adapter.get(memberId, slot),
      memberId,
    );
  };

  /**
   * Overwrite-then-remove on the pending slot, best-effort: a tombstone parses
   * as "no copy" to every build, so a remove that fails leaves nothing a
   * hydration would adopt (the two-step `neutraliseSlot` makes the same
   * argument for the committed slot).
   */
  const forgetPendingCopy = async memberId => {
    try {
      const adapter = resolveAdapter();
      await adapter.put(memberId, serialisePlatformTombstone(memberId), PLATFORM_COPY_SLOT.PENDING);
      await adapter.remove(memberId, PLATFORM_COPY_SLOT.PENDING);
    } catch (error) {
      console.warn('[CoursesKeys] The pending platform copy was not cleared:', error);
    }
  };

  /**
   * The platform's label and kind, within the budget. `describeThisDevice`
   * never rejects by contract and answers in milliseconds, but it is a bridge
   * call, and neither a hang nor a broken contract may hold the keys: past the
   * budget, or on a rejection, the generic label goes out instead.
   *
   * @returns {Promise<{ platform: 'ios'|'android'|'web', name: string }>}
   */
  const describeDevice = () => {
    const generic = () => ({ platform: thisPlatform(), name: FALLBACK_DEVICE_NAME });
    let timer;
    const budget = new Promise(resolve => {
      timer = setTimeout(() => resolve(generic()), describeTimeoutMs);
    });
    return Promise.race([describeThisDevice().catch(() => generic()), budget]).finally(() =>
      clearTimeout(timer),
    );
  };

  /**
   * Hands `courses-devices` what this install may register as: the persisted
   * id and public key with the platform's label and kind, or null when this
   * member has no registrable identity here (nothing stored, only the platform
   * copy, section 6.2's register-again case, or a record without a public
   * key). Only the four fields a register body carries; the private key never
   * leaves this module. A store without a `courses-devices` module (a spec
   * building this one alone) is left alone, and a description that outlives a
   * wipe or a member switch is dropped.
   *
   * @param {import('vuex').ActionContext} context
   * @param {number} own the generation the caller's state belongs to
   */
  const publishLocalDevice = async ({ dispatch, rootState, state }, own) => {
    if (!rootState || !('courses-devices' in rootState)) return;
    let device = null;
    if (state.deviceId && state.devicePublicKey) {
      const { name, platform } = await describeDevice();
      if (own !== generation) return;
      device = { id: state.deviceId, publicKey: state.devicePublicKey, name, platform };
    }
    await dispatch('courses-devices/setLocalDevice', device, { root: true });
  };

  /** Whether the keys loaded is never decided by the hand-over. */
  const tryPublishLocalDevice = (context, own) =>
    publishLocalDevice(context, own).catch(error => {
      console.warn('[CoursesKeys] Could not hand the device identity to courses-devices:', error);
    });

  /**
   * The section 6.2 startup order for one member, committing only while its
   * generation is still the live one.
   *
   * @param {import('vuex').ActionContext} context
   * @param {number} memberId
   * @param {number} own the generation this run belongs to
   */
  const runHydration = async (context, memberId, own) => {
    const { commit } = context;
    const live = () => own === generation;
    try {
      // Eligibility is re-probed on every hydration: the answer can change
      // (iCloud Keychain toggled, a screen lock removed) and the `eligible`
      // getter reads whatever the adapter learned last.
      const adapter = resolveAdapter();
      await adapter.probe().catch(error => {
        console.warn('[CoursesKeys] Platform key probe failed:', error);
      });
      if (!live()) return;
      const { record, persistent } = await readRecord(memberId);
      if (!live()) return;
      commit('setPersistent', persistent);
      // The attempt beside the record, and only as a state: no guard on the
      // platform copy is needed, because the copy hydration reads is written
      // only after the server has committed (the provisional one has its own
      // slot, platform-copy.js), so this order stays byte-for-byte what it was.
      commit('setAttemptState', await readAttemptState(memberId));
      if (!live()) return;
      if (record) {
        commit('setKeys', record);
      } else {
        const copy = await readPlatformCopy(adapter, memberId);
        if (!live()) return;
        if (copy) {
          // And the generation a rotation this member began may be pending
          // under (spec 6.12 step 1). Only worth reading beside a committed
          // copy: MK2 alone can finish nothing, since the walk reads MK1 rows.
          // Whether the server still has that rotation is `resumeStateFor`'s
          // question, and a generation the server dropped is dropped here on
          // its answer, exactly as the IndexedDB copy of it would be.
          const pending = await readPlatformCopy(adapter, memberId, PLATFORM_COPY_SLOT.PENDING);
          if (!live()) return;
          commit('setKeys', {
            ...copy,
            needsRegistration: true,
            pending: pending ? { keyId: pending.keyId, mk: pending.mk, keepDeviceIds: [] } : null,
          });
        }
      }
      await tryPublishLocalDevice(context, own);
      if (!live()) return;
      commit('setStatus', { status: HYDRATION_STATUS.HYDRATED });
      gate.resolve();
    } catch (error) {
      if (!live()) return;
      commit('setStatus', {
        status: HYDRATION_STATUS.FAILED,
        error: String(error?.message ?? error),
      });
      gate.reject(
        new CoursesKeysError(
          COURSES_KEYS_ERRORS.HYDRATION_FAILED,
          'The Next Steps keys could not be loaded on this device',
        ),
      );
      throw error;
    }
  };

  return {
    namespaced: true,
    state: initialState,
    getters: {
      memberId: state => state.memberId,
      /**
       * Whether THIS device holds the member's master key. It asks what the
       * bytes are, not whether the field is set: a device identity registered
       * and waiting for approval is a record with no MK, and a corrupted
       * record is one with something that is not MK. Either would have passed
       * a `!== null` test, and the sealing hook reads this getter to decide
       * whether a write can be sealed at all (#1582).
       */
      hasMasterKey: state => isMasterKey(state.mk),
      /**
       * The pending generation's key material, or null (#1694).
       *
       * Asks what the bytes ARE, exactly as `hasMasterKey` does, because a
       * device that reads as holding MK2 when it does not would sign a
       * `rotate/commit` — the operation that revokes every other device and
       * cannot be undone — for a generation it cannot then read.
       */
      pendingKeyMaterial: state =>
        isMasterKey(state.pendingMk) && state.pendingKeyId
          ? { mk: state.pendingMk, keyId: state.pendingKeyId }
          : null,
      /** Whether this device is the one that can continue a rotation. */
      holdsPendingGeneration: (state, getters) => getters.pendingKeyMaterial !== null,
      /** The other devices the pending rotation keeps; [] when it signs them all out. */
      pendingKeepDeviceIds: (state, getters) =>
        getters.pendingKeyMaterial ? state.pendingKeepDeviceIds : [],
      /**
       * Every generation this device can OPEN a row under (#1694).
       *
       * Both, while a rotation is running: the member's rows are split across
       * the two until the re-seal finishes, and a device that could read only
       * one of them would show half a member's notes as unreadable in the
       * middle of an operation that has destroyed nothing.
       *
       * The reader picks by the envelope's `kid` rather than trying each in
       * turn — the envelope names its generation, so there is nothing to guess
       * and a genuine "no key" stays distinguishable from "the wrong one".
       */
      readGenerations: (state, getters) =>
        [getters.keyMaterial, getters.pendingKeyMaterial].filter(Boolean),
      /**
       * The generation a WRITE must be sealed under, or null (#1694).
       *
       * Once a rotation has begun, the API refuses any `kid` but the pending
       * one (api#1133) — so this is not a preference, it is the only value the
       * server will accept, and sealing under the committed generation would
       * lose the write with a 409 the member cannot act on.
       *
       * Null when a rotation is running and this device does NOT hold the
       * pending generation: that device cannot write at all until the rotation
       * ends, which is the read-only face section 6.12 gives the member's
       * other phones. `rotationInProgress` is what tells the two nulls apart.
       */
      writeGeneration: (state, getters, rootState, rootGetters) => {
        if (!rootGetters['courses-devices/pendingKeyId']) return getters.keyMaterial;
        // ⚠️ WHILE THE ROTATION IS BEING UNWOUND THE GATE IS INVERTED
        // (api#1148): the API accepts the COMMITTED generation and refuses the
        // pending one, which is the exact mirror of the rule above. Sealing
        // under the pending generation there would lose every write to a 409
        // and re-fill the generation the abort is emptying.
        if (rootGetters['courses-devices/rotationAborting']) return getters.keyMaterial;
        return getters.pendingKeyMaterial;
      },
      /** Whether the SERVER says a rotation is running, whatever this device holds. */
      rotationInProgress: (state, getters, rootState, rootGetters) =>
        !!rootGetters['courses-devices/pendingKeyId'],
      isHydrated: state => state.status === HYDRATION_STATUS.HYDRATED,
      hydrationStatus: state => state.status,
      keyMaterial: state => (isMasterKey(state.mk) ? { mk: state.mk, keyId: state.keyId } : null),
      deviceIdentity: state =>
        state.deviceId
          ? {
              deviceId: state.deviceId,
              devicePublicKey: state.devicePublicKey,
              devicePrivateKey: state.devicePrivateKey,
              // Beside the pair, because every caller that needs one needs the
              // other: screen 2 shows it, and `adoptWrappedKey` opens a `v: 2`
              // wrapping with it. Null on a record written before #1693, which
              // `ensureLocalIdentity` back-fills.
              approvalSecret: state.approvalSecret,
            }
          : null,
      /**
       * Whether this platform can carry the key (section 6.8). Read live from
       * the adapter, whose answer on the web build follows the feature-flag
       * map, so a flag that loads after hydration is not missed.
       */
      eligible: () => resolveAdapter().isEligible(),
      /** One of ATTEMPT_STATUS. A state only: nothing here can turn it into a key. */
      attemptState: state => state.attemptState,
    },
    mutations: {
      reset(state) {
        Object.assign(state, initialState());
      },
      setMember(state, memberId) {
        state.memberId = memberId;
      },
      setPersistent(state, persistent) {
        state.persistent = !!persistent;
      },
      setStatus(state, { status, error = null }) {
        state.status = status;
        state.hydrationError = error;
      },
      setKeys(
        state,
        {
          mk,
          keyId,
          deviceId = null,
          devicePublicKey = null,
          devicePrivateKey = null,
          approvalSecret = null,
          needsRegistration = false,
          pending = null,
        },
      ) {
        // Anything that is not 32 bytes is not a key, however it got here: the
        // device identity is kept, the key is not, and the getters then say
        // this device holds none rather than half of one (#1582).
        const usable = isMasterKey(mk);
        if (mk !== null && mk !== undefined && !usable) {
          console.warn('[CoursesKeys] Ignoring a stored master key that is not 32 bytes:', {
            memberId: state.memberId,
          });
        }
        state.mk = usable ? mk : null;
        state.keyId = usable ? keyId : null;
        state.deviceId = deviceId;
        state.devicePublicKey = devicePublicKey;
        state.devicePrivateKey = devicePrivateKey;
        // Kept even when the key above was dropped, and deliberately so: S
        // belongs to the identity, not to MK. A record whose `mk` is corrupt
        // is still a device that registered, is still waiting for approval,
        // and still has to show the code the approver reads — dropping S with
        // the key would make that device unapprovable.
        state.approvalSecret = approvalSecret;
        state.needsRegistration = !!needsRegistration;
        // Held to the same standard as MK above: half a generation is worse
        // than none, because a device that thinks it holds MK2 would sign a
        // commit that revokes every other device and then be unable to read
        // anything it had re-sealed.
        const pendingUsable = !!pending && isMasterKey(pending.mk) && !!pending.keyId;
        if (pending && !pendingUsable) {
          console.warn('[CoursesKeys] Ignoring an unusable pending generation:', {
            memberId: state.memberId,
          });
        }
        state.pendingMk = pendingUsable ? pending.mk : null;
        state.pendingKeyId = pendingUsable ? pending.keyId : null;
        state.pendingKeepDeviceIds =
          pendingUsable && Array.isArray(pending.keepDeviceIds)
            ? pending.keepDeviceIds.filter(id => typeof id === 'string' && id)
            : [];
      },
      setEnrolment(state, { status, hasRecoveryCode }) {
        if (!ENROLMENT_STATUSES.includes(status)) {
          throw new TypeError(`Unknown enrolment status "${status}"`);
        }
        state.enrolmentStatus = status;
        state.hasRecoveryCode = !!hasRecoveryCode;
      },
      setAttemptState(state, attemptState) {
        if (!ATTEMPT_STATUSES.includes(attemptState)) {
          throw new TypeError(`Unknown attempt state "${attemptState}"`);
        }
        state.attemptState = attemptState;
      },
    },
    actions: {
      /**
       * Runs the startup order for a member. Idempotent per member while a
       * hydration is in flight or has succeeded; a failed one retries; a
       * different member id clears everything first so nothing of the previous
       * member survives in memory.
       */
      async hydrate(context, { memberId }) {
        assertMemberId(memberId);
        if (hydration.memberId === memberId && hydration.promise) return hydration.promise;
        generation += 1;
        renewGate();
        zeroBytes(context.state.mk);
        context.commit('reset');
        // The previous member's identity must not be registrable meanwhile.
        tryPublishLocalDevice(context, generation);
        context.commit('setMember', memberId);
        context.commit('setStatus', { status: HYDRATION_STATUS.HYDRATING });
        const promise = runHydration(context, memberId, generation);
        hydration = { memberId, promise };
        // A failed run must not stay cached, whether it threw before or after
        // its first await, so the next hydrate for this member retries.
        promise.catch(() => {
          if (hydration.promise === promise) hydration = { memberId: null, promise: null };
        });
        return promise;
      },
      /**
       * Re-asks the platform whether it can carry the key. Hydration probes
       * once; the nudge and the app-resume hook call this so a setting the
       * member changed while the app was in the background is seen.
       */
      async probe() {
        return resolveAdapter().probe();
      },
      /**
       * Resolves once the current member's hydration has run; while idle (no
       * member yet, or just wiped) it waits for the next one, so a read never
       * proceeds against an empty or a previous member's store. Rejects with
       * HYDRATION_FAILED, or TIMEOUT when `timeoutMs` is given and passes.
       *
       * @param {{ timeoutMs?: number }} [options]
       */
      ready(_, { timeoutMs } = {}) {
        if (!timeoutMs) return gate.promise;
        let timer;
        const timeout = new Promise((_resolve, reject) => {
          timer = setTimeout(
            () =>
              reject(
                new CoursesKeysError(
                  COURSES_KEYS_ERRORS.TIMEOUT,
                  `The Next Steps keys were not ready within ${timeoutMs} ms`,
                ),
              ),
            timeoutMs,
          );
        });
        return Promise.race([gate.promise, timeout]).finally(() => clearTimeout(timer));
      },
      /**
       * Writes new key material for the hydrated member and keeps it in memory.
       * Refuses a store that cannot keep the key, so enrolment never completes
       * against a session-only fallback, and undoes its own write if the member
       * signed out while it was in flight.
       */
      async persist(context, record) {
        const { commit, state } = context;
        const own = generation;
        const store = await writableStoreFor(state, record?.memberId);
        // A restored phone registering the key it hydrated from the platform
        // copy may hold a pending generation from the same place; it goes
        // into the record with the rest, or the promotion would find nothing.
        const carried =
          record?.pending === undefined && state.pendingMk && state.memberId === record?.memberId
            ? {
                keyId: state.pendingKeyId,
                mk: state.pendingMk,
                keepDeviceIds: [...(state.pendingKeepDeviceIds ?? [])],
              }
            : (record?.pending ?? null);
        record = { ...record, pending: carried };
        await store.put(record);
        if (own !== generation) {
          await store.remove(record.memberId).catch(() => {});
          throw signedOutMeanwhile();
        }
        commit('setKeys', { ...record, needsRegistration: false });
        await tryPublishLocalDevice(context, own);
      },
      /**
       * Keep the pending generation BEFORE the server is told about it
       * (#1694, spec 6.12).
       *
       * ⚠️ THE ORDER IS THE WHOLE POINT, and it is the opposite of enrolment's.
       * A crash between this and `POST rotate/begin` leaves a device holding
       * MK2 for a generation the server does not know about: harmless, and put
       * right on the next launch by dropping it, because the server still has
       * every row on MK1 and still accepts writes under it.
       *
       * The other order cannot be recovered from at all. `rotate/begin` first
       * would leave the server with `pendingKeyId` set and NO device anywhere
       * holding MK2 — commit and abort are both signed by the pending key, so
       * neither could ever be made, and `EncryptionModeGuard` refuses every
       * write until one of them is. The member would be locked out of writing
       * for good, by a crash in a one-line window.
       */
      async keepPendingGeneration({ commit, state }, { keyId, mk, keepDeviceIds = [] }) {
        const store = await writableStoreFor(state, state.memberId);
        const current = await store.get(state.memberId);
        if (!current) {
          throw new CoursesKeysError(
            COURSES_KEYS_ERRORS.NO_RECORD,
            'This device holds no key record to rotate from',
          );
        }
        /*
         * ⚠️ NEVER OVER A DIFFERENT ONE. This device may be the only place MK2
         * exists — the server holds it wrapped, never in the clear — and a
         * second begin tapped while the first was still pending used to write
         * MK3 over it here, a step BEFORE the server refused the begin, and
         * then drop MK3 on the refusal. Net: MK2 gone from the phone that
         * started the rotation, every row the walk had moved unreadable on it,
         * and nothing on the device able to finish or cancel. Reported on
         * 2026-09-10 as "my master device got locked out as well".
         *
         * The same id is fine (a retry of the same begin); a different one is
         * refused, and the caller has to finish or abandon what it holds first.
         */
        if (current.pending?.keyId && current.pending.keyId !== keyId) {
          throw new CoursesKeysError(
            COURSES_KEYS_ERRORS.PENDING_HELD,
            'This device already holds a pending generation; finish or cancel it first',
          );
        }
        const next = {
          ...current,
          pending: { keyId, mk, keepDeviceIds: keepDeviceIds.filter(Boolean) },
        };
        await store.put(next);
        commit('setKeys', { ...next, needsRegistration: false });
        // Spec 6.12 step 1: MK2 into the platform copy beside MK1, so a phone
        // restored from this one's iCloud/Google backup can finish or abort
        // the rotation. Its own slot; the committed one is untouched until
        // the server commits. Best-effort, like every platform write here.
        try {
          await resolveAdapter().put(
            state.memberId,
            serialisePlatformCopy({ memberId: state.memberId, keyId, mk }),
            PLATFORM_COPY_SLOT.PENDING,
          );
        } catch (error) {
          console.warn('[CoursesKeys] The pending platform copy was not written:', error);
        }
        return next;
      },
      /**
       * Commit's local half: MK2 becomes the committed generation and MK1 is
       * forgotten (#1694).
       *
       * Runs AFTER the server's commit, never before. The server is what makes
       * MK1 unrecoverable; a device that dropped it first would be unable to
       * read its own rows — every one of which is still on MK1 — if the commit
       * then failed.
       */
      async promoteGeneration({ commit, state }) {
        const store = await writableStoreFor(state, state.memberId);
        const next = await store.promoteGeneration(state.memberId);
        if (!next) return null;
        commit('setKeys', { ...next, needsRegistration: false });
        /*
         * THE PLATFORM COPY MOVES WITH THE GENERATION (spec 6.12 step 3: "the
         * device then removes MK1 from the platform copy"). Until 2026-09-11
         * nothing did: the Keychain kept MK1 from enrolment through every
         * sign-out, so on the phone that ran "Sign out all other devices" and
         * then signed out of the account — which keeps the platform copy on
         * purpose, spec 6.2 — the next login hydrated a key the server no
         * longer had, the reconciler forgot it as stale, and the member was
         * asked for a recovery code on the very phone that was meant to
         * carry the key across. Written after the store, and best-effort for
         * the same reason `adoptMasterKey` writes it that way: the key is in
         * IndexedDB either way.
         */
        try {
          const adapter = resolveAdapter();
          await adapter.put(
            state.memberId,
            serialisePlatformCopy({ memberId: state.memberId, keyId: next.keyId, mk: next.mk }),
          );
        } catch (error) {
          console.warn(
            '[CoursesKeys] The platform copy was not moved to the new generation:',
            error,
          );
        }
        await forgetPendingCopy(state.memberId);
        return next;
      },
      /**
       * Abort's local half, and the tidy-up for a `begin` the server never
       * heard about: the pending generation is forgotten and the committed one
       * is untouched, byte for byte (#1694).
       */
      async dropPendingGeneration({ commit, state }) {
        const store = await writableStoreFor(state, state.memberId);
        const next = await store.dropPending(state.memberId);
        if (!next) return null;
        commit('setKeys', { ...next, needsRegistration: false });
        await forgetPendingCopy(state.memberId);
        return next;
      },
      /**
       * The provisional attempt's row, bytes included, for the enrolment path
       * and nothing else. Null when there is none. Reads through to the store
       * every time, so it never hands out a copy a wipe already removed.
       *
       * @returns {Promise<import('@/utils/courses-crypto/key-store').AttemptRecord | null>}
       */
      async loadAttempt({ commit, state }) {
        const { memberId } = state;
        if (!memberId) return null;
        const store = await getStore();
        const attempt = await store.getAttempt(memberId);
        commit('setAttemptState', attempt?.state ?? ATTEMPT_STATUS.NONE);
        return attempt;
      },
      /**
       * Writes a new attempt for the hydrated member, `prepared`. The same
       * guards as `persist` — member, persistence, the generation — because it
       * is the same kind of write: key material that must survive a launch.
       * Sets no key field.
       */
      async prepareAttempt({ commit, state }, attempt) {
        const own = generation;
        const store = await writableStoreFor(state, attempt?.memberId);
        const row = { ...attempt, state: ATTEMPT_STATUS.PREPARED };
        await store.putAttempt(row);
        if (own !== generation) {
          await store.removeAttempt(row.memberId).catch(warnCompensation);
          throw signedOutMeanwhile();
        }
        commit('setAttemptState', ATTEMPT_STATUS.PREPARED);
      },
      /**
       * Moves the existing row forward — `ambiguous` before a send, `rejected`
       * after a 400, a fresh device id after DEVICE_ID_IN_USE — and returns
       * what is now stored. The write lands before the state is reported, so a
       * caller that sends after this resolves sends against a durable record.
       *
       * @returns {Promise<import('@/utils/courses-crypto/key-store').AttemptRecord>}
       */
      async updateAttempt({ commit, state }, patch) {
        const store = await writableStoreFor(state, state.memberId);
        const current = await store.getAttempt(state.memberId);
        if (!current) {
          throw new CoursesKeysError(
            COURSES_KEYS_ERRORS.NO_ATTEMPT,
            'There is no enrolment attempt to update',
          );
        }
        const next = { ...current, ...patch, memberId: state.memberId };
        await store.putAttempt(next);
        commit('setAttemptState', next.state);
        return next;
      },
      /**
       * The attempt becomes the key record in ONE store transaction, so the
       * two can never disagree and no later wipe can misread one for the other.
       * On a generation bump after the transaction the compensation removes
       * the key record AND restores the attempt row, so a member who signed
       * out mid-promotion still has a resumable attempt when they sign back in.
       *
       * `needsRegistration` is for the one case where the server holds this
       * attempt's bundle but not this install's device row: the key is live,
       * and the ordinary self-registration takes it from there.
       */
      async promoteAttempt(context, { attempt, needsRegistration = false }) {
        const { commit, state } = context;
        const own = generation;
        const store = await writableStoreFor(state, attempt?.memberId);
        // Its own buffer: the attempt's bytes belong to the caller, who may
        // zero them, and the live key must not go with them.
        const record = {
          memberId: attempt.memberId,
          mk: new Uint8Array(attempt.mk),
          keyId: attempt.keyId,
          deviceId: attempt.deviceId,
          devicePublicKey: attempt.devicePublicKey,
          devicePrivateKey: attempt.devicePrivateKey,
        };
        await store.promote(record);
        if (own !== generation) {
          await store.remove(record.memberId).catch(warnCompensation);
          await store.putAttempt(attempt).catch(warnCompensation);
          throw signedOutMeanwhile();
        }
        commit('setKeys', { ...record, needsRegistration });
        commit('setAttemptState', ATTEMPT_STATUS.NONE);
        await tryPublishLocalDevice(context, own);
      },
      /**
       * Removes the row. Only the enrolment reconcile calls this, and only on
       * proof (spec 6.7: "no response code authorises a discard"); zeroing
       * the bytes it holds in memory is that caller's job.
       */
      async discardAttempt({ commit, state }) {
        const { memberId } = state;
        if (!memberId) return;
        const store = await getStore();
        await store.removeAttempt(memberId);
        commit('setAttemptState', ATTEMPT_STATUS.NONE);
      },
      /**
       * Takes a platform copy as this member's key, exactly as hydration does
       * for the committed slot: in memory, flagged for registration, and NOT
       * written to the key store — the self-approval path persists it once
       * the API has answered (device-approval.js says why). For the
       * provisional slot, whose copy may be adopted only on a status read
       * showing a live bundle under its keyId.
       */
      async adoptCopy(context, { keyId, mk }) {
        const { commit, state } = context;
        if (!state.memberId) {
          throw new CoursesKeysError(COURSES_KEYS_ERRORS.WRONG_MEMBER, 'No member is hydrated');
        }
        const own = generation;
        commit('setKeys', { keyId, mk, needsRegistration: true });
        await tryPublishLocalDevice(context, own);
      },
      /**
       * Forget the member's keys on this device. The platform copies stay,
       * both of them: they are keyed by member id and belong to the phone's
       * owner. What else goes depends on the scope (WIPE_SCOPE).
       */
      async wipe(context, { scope = WIPE_SCOPE.LOGOUT } = {}) {
        if (!WIPE_SCOPES.includes(scope)) throw new TypeError(`Unknown wipe scope "${scope}"`);
        const { commit, state } = context;
        const { memberId, mk, approvalSecret } = state;
        generation += 1;
        hydration = { memberId: null, promise: null };
        renewGate();
        commit('reset');
        zeroBytes(mk);
        // S with the key it belongs beside. `reset` drops the reference; only
        // this removes the bytes from a buffer the heap is still holding.
        zeroBytes(approvalSecret);
        await tryPublishLocalDevice(context, generation);
        if (!memberId) return;
        try {
          const store = await getStore();
          await store.remove(memberId);
          if (scope === WIPE_SCOPE.LOGOUT) await store.removeAttempt(memberId);
        } catch (error) {
          console.warn('[CoursesKeys] Could not remove the key record:', error);
        }
      },
    },
  };
}

export default createCoursesKeysModule();
